import axios from "axios";

// Token helpers - use localStorage since Firebase CDN strips cookies
export const getToken = () => localStorage.getItem("access_token");
export const setToken = (token) => localStorage.setItem("access_token", token);
export const clearToken = () => localStorage.removeItem("access_token");

const api = axios.create({ baseURL: "/api" });

// Attach Bearer token to every request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

// Global response interceptor for Subscription Lockouts (402)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 402) {
      if (window.location.pathname !== "/billing") {
        window.location.href = "/billing";
      }
    }
    return Promise.reject(error);
  }
);

// Auth - extract and store token from response
export const authLogin = (email, password, inviteToken = "") =>
  api.post("/auth/login", { email, password, invite_token: inviteToken || undefined }).then(r => {
    if (r.data.access_token) setToken(r.data.access_token);
    return r.data;
  });
export const authRegister = (data) =>
  api.post("/auth/register", data).then(r => {
    if (r.data.access_token) setToken(r.data.access_token);
    return r.data;
  });
export const authLogout = () => { clearToken(); return api.post("/auth/logout").then(r => r.data); };
export const authMe = () => api.get("/auth/me").then(r => r.data);
export const fetchInviteDetails = (token) => api.get(`/auth/invite/${token}`).then(r => r.data);
export const acceptInvite = (token, data) =>
  api.post("/auth/accept-invite", { token, ...data }).then(r => {
    if (r.data.access_token) setToken(r.data.access_token);
    return r.data;
  });
export const forgotPassword = (email) => api.post("/auth/forgot-password", { email }).then(r => r.data);
export const resetPassword = (token, password) => api.post("/auth/reset-password", { token, password }).then(r => r.data);

// Users
export const fetchUsers = () => api.get("/users").then(r => r.data);

// Vehicles
export const fetchVehicles = (params) => api.get("/vehicles", { params }).then(r => r.data);
export const createVehicle = (data) => api.post("/vehicles", data).then(r => r.data);
export const fetchVehicle = (id) => api.get(`/vehicles/${id}`).then(r => r.data);
export const updateVehicle = (id, data) => api.patch(`/vehicles/${id}`, data).then(r => r.data);
export const deleteVehicle = (id) => api.delete(`/vehicles/${id}`).then(r => r.data);

// Customers
export const fetchCustomers = (q) => api.get("/customers", { params: q ? { q } : {} }).then(r => r.data);
export const createCustomer = (data) => api.post("/customers", data).then(r => r.data);
export const fetchCustomer = (id) => api.get(`/customers/${id}`).then(r => r.data);
export const updateCustomer = (id, data) => api.patch(`/customers/${id}`, data).then(r => r.data);

// Rentals
export const fetchRentals = (params) => api.get("/rentals", { params }).then(r => r.data);
export const createRental = (data) => api.post("/rentals", data).then(r => r.data);
export const fetchRental = (id) => api.get(`/rentals/${id}`).then(r => r.data);
export const updateRental = (id, data) => api.patch(`/rentals/${id}`, data).then(r => r.data);

// Agreements
export const fetchAgreements = (params) => api.get("/agreements", { params }).then(r => r.data);
export const createAgreement = (rentalId) => api.post(`/rentals/${rentalId}/agreement`).then(r => r.data);
export const sendAgreement = (agreementId) => api.post(`/agreements/${agreementId}/send`).then(r => r.data);
export const fetchAgreementTemplate = () => api.get("/agreements/template").then(r => r.data);
export const updateAgreementTemplate = (data) => api.put("/agreements/template", data).then(r => r.data);

// Public signing (no auth needed)
export const getSigningData = (token) => api.get(`/sign/${token}`).then(r => r.data);
export const submitSignature = (token, payload) => api.post(`/sign/${token}`, payload).then(r => r.data);
export const uploadSigningFile = (token, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/sign/${token}/uploads`, formData, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};

// Inspections
export const fetchInspections = (rentalId) => api.get(`/rentals/${rentalId}/inspections`).then(r => r.data);
export const createInspection = (rentalId, data) => api.post(`/rentals/${rentalId}/inspections`, data).then(r => r.data);

// Activity
export const fetchActivity = (rentalId) => api.get(`/rentals/${rentalId}/activity`).then(r => r.data);
export const createActivityNote = (rentalId, content) => api.post(`/rentals/${rentalId}/activity`, { content }).then(r => r.data);

// Dashboard
export const fetchDashboard = () => api.get("/dashboard").then(r => r.data);

// Returns
export const fetchReturns = () => api.get("/returns").then(r => r.data);

// Search
export const searchAll = (q) => api.get("/search", { params: { q } }).then(r => r.data);

// Admin Settings
export const fetchSettings = () => api.get("/admin/settings").then(r => r.data);
export const updateSettings = (data) => api.put("/admin/settings", data).then(r => r.data);
export const testSlack = (data) => api.post("/admin/slack-test", data).then(r => r.data);
export const testEmail = (data) => api.post("/admin/email-test", data).then(r => r.data);
export const sendSupportMessage = (message) => api.post("/admin/support", { message }).then(r => r.data);

// Invoice APIs
export const fetchRentalInvoices = (rentalId) => api.get(`/rentals/${rentalId}/invoices`).then(r => r.data);
export const createInvoice = (rentalId, data) => api.post(`/rentals/${rentalId}/invoices`, data).then(r => r.data);
export const sendInvoiceEmail = (invoiceId) => api.post(`/invoices/${invoiceId}/send`).then(r => r.data);
export const updateInvoiceStatus = (invoiceId, data) => api.patch(`/invoices/${invoiceId}`, data).then(r => r.data);

// PDF Downloads — must be fetched via axios (Bearer token), not plain <a href>
export const downloadAgreementPdf = async (agreementId, filename) => {
  const res = await api.get(`/agreements/${agreementId}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `agreement-${agreementId}.pdf`;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
};
export const downloadInvoicePdf = async (invoiceId, filename) => {
  const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `invoice-${invoiceId}.pdf`;
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
};
// Keep URL helpers for any direct embed use-cases (e.g. img src backed by session)
export const getInvoicePdfUrl = (invoiceId) => `/api/invoices/${invoiceId}/pdf`;
export const getAgreementPdfUrl = (agreementId) => `/api/agreements/${agreementId}/pdf`;

// Organization APIs
export const fetchOrganizations = () => api.get("/organizations").then(r => r.data);
export const createOrganization = (data) => api.post("/organizations", data).then(r => r.data);
export const updateOrganization = (orgId, data) => api.patch(`/organizations/${orgId}`, data).then(r => r.data);
export const switchOrganization = (orgId) => api.post(`/organizations/${orgId}/switch`).then(r => r.data);

// Team APIs
export const fetchOrgMembers = (orgId) => api.get(`/organizations/${orgId}/members`).then(r => r.data);
export const inviteMember = (orgId, data) => api.post(`/organizations/${orgId}/invite`, data).then(r => r.data);
export const updateMemberRole = (orgId, membershipId, data) => api.patch(`/organizations/${orgId}/members/${membershipId}`, data).then(r => r.data);
export const removeMember = (orgId, membershipId) => api.delete(`/organizations/${orgId}/members/${membershipId}`).then(r => r.data);

// Billing APIs
export const fetchBillingInfo = () => api.get("/billing/info").then(r => r.data);
export const createCheckoutSession = (data) => api.post("/billing/checkout", data).then(r => r.data);
export const checkPaymentStatus = (sessionId) => api.get(`/billing/status/${sessionId}`).then(r => r.data);
export const cancelSubscription = () => api.post("/billing/cancel").then(r => r.data);
export const reactivateSubscription = () => api.post("/billing/reactivate").then(r => r.data);
export const createPortalSession = (data) => api.post("/billing/portal", data).then(r => r.data);
export const fetchStripeInvoices = () => api.get("/billing/invoices").then(r => r.data);

// Super Admin APIs
export const fetchTenants = () => api.get("/admin/tenants").then(r => r.data);
export const toggleTenantStatus = (orgId) => api.patch(`/admin/tenants/${orgId}`).then(r => r.data);

// Upload APIs (GridFS)
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post("/uploads", formData, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
};
export const getUploadUrl = (fileId) => `/api/uploads/${fileId}`;
export const deleteUpload = (fileId) => api.delete(`/uploads/${fileId}`).then(r => r.data);

export default api;
